// Khung ký tên bằng ngón tay / bút / chuột (pointer events, nét mượt, đúng tỉ lệ màn hình độ phân giải cao)
import { useEffect, useRef, useState } from 'react';
import { Eraser } from 'lucide-react';

interface Props {
  /** Trả ảnh PNG sau mỗi nét; null khi xóa */
  onChange: (dataUrl: string | null) => void;
  height?: number;
  placeholder?: string;
}

export default function SignaturePad({ onChange, height = 180, placeholder = 'Khách ký vào khung này' }: Props) {
  const ref = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const last = useRef<{ x: number; y: number } | null>(null);
  const [empty, setEmpty] = useState(true);

  // Đặt kích thước thật của canvas theo khung hiển thị × DPR
  useEffect(() => {
    const c = ref.current!;
    // Dùng kích thước layout (clientWidth) — không bị ảnh hưởng bởi hiệu ứng phóng to của Modal
    const fit = () => {
      const dpr = window.devicePixelRatio || 1;
      if (!c.clientWidth || c.width === Math.round(c.clientWidth * dpr)) return;
      c.width = Math.round(c.clientWidth * dpr);
      c.height = Math.round(c.clientHeight * dpr);
      const ctx = c.getContext('2d')!;
      ctx.scale(dpr, dpr);
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = '#0f172a';
      setEmpty(true);
      onChange(null);
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(c);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pos = (e: React.PointerEvent) => {
    const c = ref.current!;
    const r = c.getBoundingClientRect();
    return { x: (e.clientX - r.left) * (c.clientWidth / r.width), y: (e.clientY - r.top) * (c.clientHeight / r.height) };
  };

  const down = (e: React.PointerEvent) => {
    e.preventDefault();
    ref.current!.setPointerCapture(e.pointerId);
    drawing.current = true;
    last.current = pos(e);
    const ctx = ref.current!.getContext('2d')!;
    ctx.beginPath();
    ctx.arc(last.current.x, last.current.y, 1.2, 0, Math.PI * 2);
    ctx.fillStyle = '#0f172a';
    ctx.fill();
  };
  const move = (e: React.PointerEvent) => {
    if (!drawing.current || !last.current) return;
    const p = pos(e);
    const ctx = ref.current!.getContext('2d')!;
    // Nét dày theo lực bút (chuột/ngón tay mặc định 0.5)
    ctx.lineWidth = 1.6 + (e.pressure || 0.5) * 2;
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
  };
  const up = () => {
    if (!drawing.current) return;
    drawing.current = false;
    last.current = null;
    setEmpty(false);
    onChange(trimmed(ref.current!));
  };
  const clear = () => {
    const c = ref.current!;
    c.getContext('2d')!.clearRect(0, 0, c.width, c.height);
    setEmpty(true);
    onChange(null);
  };

  return (
    <div className="relative select-none">
      <canvas ref={ref} style={{ height, touchAction: 'none' }}
        className="block w-full cursor-crosshair rounded-lg border-2 border-dashed border-slate-300 bg-white"
        onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up} onPointerLeave={up} />
      {empty && <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-slate-400">{placeholder}</div>}
      <div className="pointer-events-none absolute bottom-6 left-6 right-6 border-b border-slate-200" />
      <button type="button" onClick={clear} disabled={empty}
        className="absolute right-2 top-2 flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-semibold text-slate-600 hover:border-red-300 hover:text-red-600 disabled:opacity-40">
        <Eraser className="h-3.5 w-3.5" /> Ký lại
      </button>
    </div>
  );
}

/** Cắt khoảng trắng quanh chữ ký để ảnh gọn nhẹ */
function trimmed(c: HTMLCanvasElement): string {
  const ctx = c.getContext('2d')!;
  const { width, height } = c;
  const data = ctx.getImageData(0, 0, width, height).data;
  let x0 = width, y0 = height, x1 = 0, y1 = 0;
  for (let y = 0; y < height; y += 2) {
    for (let x = 0; x < width; x += 2) {
      if (data[(y * width + x) * 4 + 3] > 0) {
        if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y;
      }
    }
  }
  if (x1 <= x0 || y1 <= y0) return c.toDataURL('image/png');
  const m = 8;
  x0 = Math.max(0, x0 - m); y0 = Math.max(0, y0 - m); x1 = Math.min(width, x1 + m); y1 = Math.min(height, y1 + m);
  const out = document.createElement('canvas');
  out.width = x1 - x0;
  out.height = y1 - y0;
  out.getContext('2d')!.drawImage(c, x0, y0, out.width, out.height, 0, 0, out.width, out.height);
  return out.toDataURL('image/png');
}
